import React, {useCallback, useLayoutEffect, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Box, Typography} from "@mui/material";
import Divider from "@mui/material/Divider";
import {ProductOtherPartnerAvailabilityCard, ProductWarehouseCard} from "../../../../product-cards/product-warehouse-card";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const AvailabilitySection = ({partNo, partId, cache, updateCache}) => {
    const operatorAccessLevels = useAccessLevels()
    const [availabilityLoading, setAvailabilityLoading] = useState(!cache.availability);
    const [availability, setAvailability] = useState({
        warehouses: cache.availability?.warehouses ?? [],
        otherPartners: cache.availability?.otherPartners ?? [],
        schedules: cache.availability?.schedules ?? [],
    });
    const [shippingCompanies, setShippingCompanies] = useState(cache.shippingCompanies ?? []);
    const isMounted = useIsMounted();

    /**
     * Fetches the warehouse schedules from the server and if the result of the api is successful, sets the state.
     * @type {(function(*): Promise<void>)|*}
     */
    const getWarehouseSchedules = useCallback(async (warehouseIds) => {
        const response = await Api.getPartnerOperationHours({warehouseIds: warehouseIds});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const allItems = response.data || [];
            if (!allItems.length) return;

            // Group by warehouseId
            const schedules = {};
            for (const item of allItems) {
                if (!schedules[item.warehouseId])
                    schedules[item.warehouseId] = [];
                schedules[item.warehouseId].push(item);
            }
            setAvailability(prevState => ({
                ...prevState,
                schedules: schedules
            }));
            return schedules;
        }
    }, [isMounted])

    /**
     * Fetches the part's availability from the server and if the result of the api is successful, sets the state.
     * @return {Promise<void>}
     */
    const getPartAvailability = useCallback(async () => {
        setAvailabilityLoading(true);
        const [availabilityResponse, warehousesResponse] = await Promise.all([
            await Api.getProductAvailabilitiesV2({partNumbers: [partNo]}),
            await Api.getOperatorWarehouses(),
        ])
        if (!isMounted())
            return;
        if (availabilityResponse?.resultFlag && warehousesResponse?.resultFlag) {
            const data = availabilityResponse.data?.[0];
            const otherPartners = data?.partners?.filter(e => !e.isMainPartner) ?? [];
            const availability = {
                warehouses: data?.localWarehouses?.map(e => ({localWarehouse: e, spireWarehouse: warehousesResponse.data?.find(l => l.id === e.warehouseId)})) ?? [],
                otherPartners: otherPartners,
            };
            setAvailability(availability)
            let schedules;
            if (operatorAccessLevels.packages.sales.warehouseSchedules) {
                const allWarehouseIds = Array.from(new Set(data?.partners?.map(e => e.warehouses)?.flat()?.map(w => w.id) ?? [])).filter(Boolean);
                if (!!allWarehouseIds.length) {
                    schedules = await getWarehouseSchedules(allWarehouseIds);
                    availability.schedules = schedules;
                }
            }
            updateCache('availability', availability);
        }
        setAvailabilityLoading(false);
    }, [getWarehouseSchedules, isMounted, operatorAccessLevels.packages.sales.warehouseSchedules, partNo, updateCache])

    /**
     * Fetch the part's availability on mount.
     */
    useLayoutEffect(() => {
        if (cache.availability) {
            setAvailability({
                warehouses: cache.availability.warehouses ?? [],
                otherPartners: cache.availability.otherPartners ?? [],
            });
            if (operatorAccessLevels.packages.sales.warehouseSchedules) {
                void getWarehouseSchedules(cache.availability.warehouses?.map(w => w.localWarehouse.warehouseId) ?? []);
            }
            setAvailabilityLoading(false);
        } else {
            void getPartAvailability();
        }
    }, [getPartAvailability, cache.availability, getWarehouseSchedules, operatorAccessLevels.packages.sales.warehouseSchedules]);

    /**
     * Fetches the shipping companies from the server and if the result of the api is successful, sets the state.
     * @type {(function(): Promise<void>)}
     */
    const getShippingCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setShippingCompanies(response?.data ?? []);
            updateCache('shippingCompanies', response?.data ?? []);
        }
    }, [isMounted, updateCache])

    /**
     * Fetch the shipping companies on mount.
     */
    useLayoutEffect(() => {
        if (cache.shippingCompanies) {
            setShippingCompanies(cache.shippingCompanies);
            return;
        }
        void getShippingCompanies();
    }, [getShippingCompanies, cache.shippingCompanies]);

    /**
     * Sets the schedule for a specific warehouse.
     * @type {(function(*, *): void)|*}
     */
    const setSchedule = useCallback((warehouseId, updater) => {
        setAvailability(prevState => {
            const found = prevState.schedules?.[warehouseId];
            const updatedSchedules = typeof updater === 'function' ? updater(found ?? []) : updater;
            const availability = {
                ...prevState,
                schedules: {
                    ...(prevState.schedules ?? {}),
                    [warehouseId]: updatedSchedules
                }
            };
            updateCache('availability', availability);
            return availability;
        })
    }, [updateCache])
    return (
        <>
            <Box className={'d-flex flex-column'}>
                {
                    availabilityLoading &&
                    <div className='loading part-warehouse mb-5'>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                    </div>
                }
                {
                    !availabilityLoading &&
                    <>
                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                            {
                                availability?.warehouses?.map(warehouse => (
                                    <ProductWarehouseCard
                                        viewOnly
                                        key={warehouse.localWarehouse.warehouseId ?? warehouse.spireWarehouse?.code}
                                        localWarehouse={warehouse.localWarehouse}
                                        spireWarehouse={warehouse.spireWarehouse}
                                        shippingCompanies={shippingCompanies}
                                        partInfo={{partId: partId, partNo: partNo}}
                                        schedule={availability.schedules?.[warehouse.localWarehouse.warehouseId]}
                                        // setSchedule={setSchedule}
                                    />
                                ))
                            }
                        </Box>
                        {
                            !!availability.otherPartners?.length &&
                            <Box sx={{position: 'relative', marginBlock: 4}}>
                                <Divider
                                    absolute
                                    sx={{
                                        borderStyle: "dashed",
                                        borderColor: 'text.disabled',
                                        borderBottomWidth: 2,
                                        paddingInline: 3,
                                    }}
                                />
                                <Typography
                                    variant={'body2'}
                                    color={'tertiary.main'}
                                    fontWeight={600}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -65%)',
                                        backgroundColor: 'background.paper',
                                        paddingInline: 2,
                                    }}
                                >
                                    Other Partners
                                </Typography>
                            </Box>
                        }
                        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                            {
                                availability.otherPartners.map((partner) => (
                                    <ProductOtherPartnerAvailabilityCard
                                        key={partner.partnerName}
                                        partner={partner}
                                        schedules={availability.schedules}
                                    />
                                ))
                            }
                        </Box>

                    </>
                }
            </Box>
        </>
    )
}

export default AvailabilitySection;